import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import { Fade } from "react-awesome-reveal";
import "swiper/css";

import SectionTitle from "../home/SectionTitle";
import SliderLine from "../home/SliderLine";

const METRICS = [
  { valueKey: "securityPage.arch.metric1.value", labelKey: "securityPage.arch.metric1.label" },
  { valueKey: "securityPage.arch.metric2.value", labelKey: "securityPage.arch.metric2.label" },
  { valueKey: "securityPage.arch.metric3.value", labelKey: "securityPage.arch.metric3.label" },
  { valueKey: "securityPage.arch.metric4.value", labelKey: "securityPage.arch.metric4.label" },
];

const CARDS = [
  { titleKey: "securityPage.arch.card1.title", items: ["securityPage.arch.card1.item1", "securityPage.arch.card1.item2"] },
  { titleKey: "securityPage.arch.card2.title", items: ["securityPage.arch.card2.item1", "securityPage.arch.card2.item2"] },
  { titleKey: "securityPage.arch.card3.title", items: ["securityPage.arch.card3.item1", "securityPage.arch.card3.item2"] },
  { titleKey: "securityPage.arch.card4.title", items: ["securityPage.arch.card4.item1", "securityPage.arch.card4.item2"] },
];

function ArchCard({ card, t }) {
  return (
    <Fade triggerOnce direction="up" duration={600}>
      <div className="bg-white border border-[#e4edf7] rounded-[24px] h-[227px] lg:h-[320px] p-[28px] lg:p-[32px] flex flex-col">
        <p className="font-['Inter'] font-bold text-[20px] leading-[1.4] text-[#1c385f]">
          {t(card.titleKey)}
        </p>
        <div className="mt-[14px] h-px w-full bg-[#edf1f8]" />
        <div className="mt-[16px] lg:mt-[24px] flex flex-col gap-[8px] lg:gap-[29px]">
          {card.items.map((key) => (
            <div key={key} className="flex items-center gap-[11px]">
              <span className="w-[7px] h-[7px] rounded-full bg-[#4da6ff] shrink-0" />
              <span className="font-['Inter'] font-normal text-[15px] leading-[1.5] text-[#435c7f]">
                {t(key)}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-auto inline-flex items-center gap-[6px] self-start bg-[#eef3ff] rounded-full px-[11px] py-[7px]">
          <span className="w-[7px] h-[7px] rounded-full bg-[#4da6ff]" />
          <span className="font-['Inter'] font-medium text-[11px] text-[#1c385f]">
            {t("securityPage.arch.badge")}
          </span>
        </div>
      </div>
    </Fade>
  );
}

function Architecture() {
  const { t } = useTranslation();
  const swiperRef = useRef(null);
  const [current, setCurrent] = useState(0);

  return (
    <section className="mt">
      <div className="glcontainer">
        <SectionTitle
          title1={t("securityPage.arch.title1")}
          title2={t("securityPage.arch.title2")}
          text={t("securityPage.arch.subtitle")}
          className="mx-auto max-w-[548px]"
        />

        {/* Метрики */}
        <div className="mt-[25px] lg:mt-[40px] grid grid-cols-2 lg:grid-cols-4 gap-[8px] lg:gap-[13px]">
          <Fade cascade triggerOnce direction="up" damping={0.06}>
            {METRICS.map((m) => (
              <div
                key={m.labelKey}
                className="bg-[#f8faff] border border-[#e4edf7] rounded-[20px] px-[20px] lg:px-[28px] py-[18px] lg:py-[23px]"
              >
                <p className="font-['Inter'] font-black text-[24px] lg:text-[36px] leading-[1.2] text-[#1c385f]">
                  {t(m.valueKey)}
                </p>
                <p className="mt-[2px] font-['Inter'] font-bold text-[14px] leading-[1.2] text-[#1c385f]">
                  {t(m.labelKey)}
                </p>
              </div>
            ))}
          </Fade>
        </div>

        {/* Desktop: 4 карточки в ряд */}
        <div className="hidden lg:grid mt-[13px] grid-cols-4 gap-[13px]">
          {CARDS.map((card) => (
            <ArchCard key={card.titleKey} card={card} t={t} />
          ))}
        </div>

        {/* Mobile / tablet: слайдер */}
        <div className="lg:hidden mt-[16px]">
          <Swiper
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            onSlideChange={(swiper) => setCurrent(swiper.realIndex)}
            spaceBetween={16}
            slidesPerView={1}
            grabCursor
            breakpoints={{ 640: { slidesPerView: 2.15, spaceBetween: 20 } }}
          >
            {CARDS.map((card) => (
              <SwiperSlide key={card.titleKey}>
                <ArchCard card={card} t={t} />
              </SwiperSlide>
            ))}
          </Swiper>
          <SliderLine
            current={current}
            total={CARDS.length}
            onPrev={() => swiperRef.current?.slidePrev()}
            onNext={() => swiperRef.current?.slideNext()}
          />
        </div>
      </div>
    </section>
  );
}

export default Architecture;
